import { useState } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '@/services/api'
import { Card, Button, Input, Select } from '@/components/ui'
import type { CreateCanaryRequest } from '@spoofio/shared/types'

export function CanaryCreatePage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()
  const preselectedOrgId = searchParams.get('orgId')

  const [formData, setFormData] = useState<CreateCanaryRequest>({
    name: '',
    description: '',
    authorizedDomains: [''],
    screenshotEnabled: false,
    confidenceThreshold: 70,
  })
  const [selectedOrgId, setSelectedOrgId] = useState<string>(preselectedOrgId ?? '')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [showCreateOrg, setShowCreateOrg] = useState(false)
  const [newOrgName, setNewOrgName] = useState('')

  const { data: organizations, isLoading: orgsLoading } = useQuery({
    queryKey: ['organizations'],
    queryFn: () => api.listOrganizations(),
  })

  // Auto-select first org if not preselected
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- intentional: empty string should fallback
  const orgId = selectedOrgId || organizations?.[0]?.id || ''

  const createOrgMutation = useMutation({
    mutationFn: (name: string) => {
      // Auto-generate slug from name
      const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
      return api.createOrganization({ name, slug })
    },
    onSuccess: (org) => {
      void queryClient.invalidateQueries({ queryKey: ['organizations'] })
      setSelectedOrgId(org.id)
      setShowCreateOrg(false)
      setNewOrgName('')
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        setErrors({ newOrg: error.message })
      }
    },
  })

  const createMutation = useMutation({
    mutationFn: (data: CreateCanaryRequest) => api.createCanary(orgId, data),
    onSuccess: (canary) => {
      void queryClient.invalidateQueries({ queryKey: ['canaries'] })
      navigate(`/canaries/${canary.id}`)
    },
    onError: (error) => {
      if (error instanceof ApiError && error.details) {
        setErrors(
          Object.fromEntries(
            Object.entries(error.details).map(([key, messages]) => [
              key,
              messages[0],
            ])
          )
        )
      } else if (error instanceof ApiError) {
        setErrors({ submit: error.message })
      }
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setErrors({})

    // Validate name
    if (!formData.name.trim()) {
      setErrors({ name: 'Name is required' })
      return
    }

    // Filter out empty domains
    const authorizedDomains = formData.authorizedDomains.filter(
      (d) => d.trim() !== ''
    )

    if (authorizedDomains.length === 0) {
      setErrors({ authorizedDomains: 'At least one authorized domain is required' })
      return
    }

    if (!orgId) {
      setErrors({ organization: 'Please create or select an organization first' })
      return
    }

    createMutation.mutate({
      ...formData,
      authorizedDomains,
    })
  }

  const handleCreateOrg = () => {
    if (!newOrgName.trim()) {
      setErrors({ newOrg: 'Organization name is required' })
      return
    }
    createOrgMutation.mutate(newOrgName.trim())
  }

  const handleDomainChange = (index: number, value: string) => {
    const newDomains = [...formData.authorizedDomains]
    newDomains[index] = value.toLowerCase().trim()
    setFormData({ ...formData, authorizedDomains: newDomains })
  }

  const addDomainField = () => {
    setFormData({
      ...formData,
      authorizedDomains: [...formData.authorizedDomains, ''],
    })
  }

  const removeDomainField = (index: number) => {
    const newDomains = formData.authorizedDomains.filter((_, i) => i !== index)
    setFormData({
      ...formData,
      authorizedDomains: newDomains.length > 0 ? newDomains : [''],
    })
  }

  const hasNoOrgs = !orgsLoading && (!organizations || organizations.length === 0)

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Link to="/canaries" className="text-gray-400 hover:text-gray-600">
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">Create Canary</h1>
      </div>

      {/* Show create org prompt if no organizations exist */}
      {hasNoOrgs && !showCreateOrg && (
        <Card title="No Organizations Found">
          <p className="text-gray-600 mb-4">
            You need to create an organization before you can create a canary.
          </p>
          <Button onClick={() => { setShowCreateOrg(true); }}>
            Create Organization
          </Button>
        </Card>
      )}

      {/* Inline organization creation */}
      {showCreateOrg && (
        <Card title="Create Organization">
          <div className="space-y-4">
            <Input
              label="Organization Name"
              placeholder="My Company"
              value={newOrgName}
              onChange={(e) => { setNewOrgName(e.target.value); }}
              error={errors.newOrg}
            />
            <div className="flex gap-2">
              <Button
                onClick={handleCreateOrg}
                isLoading={createOrgMutation.isPending}
              >
                Create
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setShowCreateOrg(false)
                  setNewOrgName('')
                  setErrors({})
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Only show canary form if we have an organization */}
      {(!hasNoOrgs || orgId) && (
        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            {errors.submit && (
              <div className="bg-danger-50 text-danger-700 p-4 rounded-lg">
                {errors.submit}
              </div>
            )}

            <Card title="Basic Information">
              <div className="space-y-4">
                {organizations && organizations.length > 1 && (
                  <Select
                    label="Organization"
                    value={orgId}
                    onChange={(e) => { setSelectedOrgId(e.target.value); }}
                    options={organizations.map((org) => ({
                      value: org.id,
                      label: org.name,
                    }))}
                    error={errors.organization}
                  />
                )}

                {organizations?.length === 1 && (
                  <div className="text-sm text-gray-600">
                    Organization: <strong>{organizations[0].name}</strong>
                  </div>
                )}

                {errors.organization && (
                  <p className="text-sm text-danger-600">{errors.organization}</p>
                )}

                <Input
                  label="Name"
                  placeholder="Donation Page Canary"
                  value={formData.name}
                  onChange={(e) =>
                    { setFormData({ ...formData, name: e.target.value }); }
                  }
                  error={errors.name}
                />

                <Input
                  label="Description (optional)"
                  placeholder="Tracks unauthorized use of the donation page"
                  value={formData.description ?? ''}
                  onChange={(e) =>
                    { setFormData({ ...formData, description: e.target.value }); }
                  }
                  error={errors.description}
                />
              </div>
            </Card>

            <Card
              title="Authorized Domains"
              description="Domains where this canary can legitimately load without triggering alerts"
            >
              <div className="space-y-3">
                {formData.authorizedDomains.map((domain, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      placeholder="example.com"
                      value={domain}
                      onChange={(e) => { handleDomainChange(index, e.target.value); }}
                      className="flex-1"
                    />
                    {formData.authorizedDomains.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => { removeDomainField(index); }}
                      >
                        <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </Button>
                    )}
                  </div>
                ))}
                {errors.authorizedDomains && (
                  <p className="text-sm text-danger-600">{errors.authorizedDomains}</p>
                )}
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={addDomainField}
                >
                  + Add another domain
                </Button>
              </div>
            </Card>

            <Card title="Alert Settings">
              <div className="space-y-4">
                <div>
                  <label className="label">Confidence Threshold: {formData.confidenceThreshold}%</label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={formData.confidenceThreshold}
                    onChange={(e) =>
                      { setFormData({
                        ...formData,
                        confidenceThreshold: Number(e.target.value),
                      }); }
                    }
                    className="w-full"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Only send alerts when detection confidence is at or above this threshold
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="screenshotEnabled"
                    checked={formData.screenshotEnabled}
                    onChange={(e) =>
                      { setFormData({
                        ...formData,
                        screenshotEnabled: e.target.checked,
                      }); }
                    }
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="screenshotEnabled" className="text-sm text-gray-700">
                    Capture screenshots of fake pages (requires Browser Rendering API)
                  </label>
                </div>
              </div>
            </Card>

            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="secondary"
                onClick={() => { navigate('/canaries'); }}
              >
                Cancel
              </Button>
              <Button type="submit" isLoading={createMutation.isPending}>
                Create Canary
              </Button>
            </div>
          </div>
        </form>
      )}
    </div>
  )
}
